import { initializeApp } from "firebase/app";

import {
  createUserWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);


// BEGIN: AUTHENTICATION
const logInWithEmailAndPassword = async (email, password, setErrMsg) => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
  } catch (err) {
    if (err.code === "auth/invalid-email") {
      setErrMsg("Invalid email format");
    } else if (err.code === "auth/wrong-password" || err.code === "auth/user-not-found") {
      setErrMsg("Incorrect email or password");
    } else {
      setErrMsg("Something went wrong. Please try again.");
    }
  }
};

const registerWithEmailAndPassword = async (first_name, last_name, email, password, setErrMsg) => {
  try {
    const res = await createUserWithEmailAndPassword(auth, email, password);
    const user = res.user;
    await addDoc(collection(db, "Users"), {
      uid: user.uid,
      first_name,
      last_name,
      isAdmin: false,
      email,
    });
  } catch (err) {
    if (err.code === "auth/email-already-in-use") {
      setErrMsg("Email already in use");
    } else if (err.code === "auth/invalid-email") {
      setErrMsg("Invalid email format");
    } else if (err.code === "auth/weak-password") {
      setErrMsg("Password must be at least 6 characters");
    } else {
      setErrMsg("Something went wrong. Please try again.");
    }
  }
};

const sendPasswordReset = async (email, setSuccessMsg, setErrMsg) => {
  try {
    await sendPasswordResetEmail(auth, email);
    setErrMsg(false);
    setSuccessMsg("Password reset link sent to your email! If you don't see it within moments, please check your spam/junk folder.");
  } catch (err) {
    setSuccessMsg(false);
    if (err.code === "auth/invalid-email") {
      setErrMsg("Invalid email format");
    } else if (err.code === "auth/user-not-found") {
      setErrMsg("No account associated with this email");
    } else {
      setErrMsg("Something went wrong. Please try again.");
    }
  }
};

const logout = () => {
  signOut(auth);
};
// END: AUTHENTICATION


// BEGIN: GET

const getAllDocsInCollection = async (collName) => {
  const collRef = collection(db, collName);
  const querySnapshot = await getDocs(collRef);
  const documents = querySnapshot.docs.map((doc) => doc.data());
  return documents;
};

// const getDocsByCollectionAndUserIdOrIsGlobal = async (collName, userId) => {
//   const collRef = collection(db, collName);
//   const collQuery = query(collRef, where("userId", '==', userId), where('isGlobal', '==', true));
//   const snapshot = await getDocs(collQuery);
//   const docs = snapshot.docs.map((doc) => doc.data());
//   return docs;
// };

const getDocByCollectionAndId = async (collName, docId) => {
  try {
    const docRef = doc(db, collName, docId);
    const docSnap = await getDoc(docRef);
    return docSnap.data();
  } catch (err) {
    console.log(err)
  }
};

const getDocsByCollectionAndFK = async (collName, fkLabel, fkValue) => {
  const collQuery = query(collection(db, collName), where(fkLabel, "==", fkValue));
  const snapshot = await getDocs(collQuery);
  const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  return result;
};

const getDocsBySubCollectionAndIds = async (collName, parentDocId, subCollName, idsList) => {
  if (idsList.length === 0) return [];
  const docRef = doc(db, collName, parentDocId);
  const subCollQuery = query(collection(docRef, subCollName), where("id", "in", idsList));
  const snapshot = await getDocs(subCollQuery);
  const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  return result;
};

const getAllDocsBySubCollection = async (collName, parentDocId, subCollName) => {
  const docRef = doc(db, collName, parentDocId);
  const subCollQuery = query(collection(docRef, subCollName));
  const snapshot = await getDocs(subCollQuery);
  const result = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  return result;
};

const getUserByUid = async (uid) => {
  const collQuery = query(collection(db, "Users"), where("uid", "==", uid));
  const snapshot = await getDocs(collQuery);
  return snapshot.docs[0].data();
};
// END: GET


// BEGIN: UPDATE
const updateUserByUid = async (uid, data) => {
  const collQuery = query(collection(db, "Users"), where("uid", "==", uid));
  const snapshot = await getDocs(collQuery);
  if (snapshot.docs.length === 0) return;
  const docRef = snapshot.docs[0].ref;
  await updateDoc(docRef, data);
};

const updateDocByCollectionAndId = async (collName, docId, data) => {
  const docRef = doc(db, collName, docId);
  await updateDoc(docRef, data);
};

const updateDocInSubCollection = async (collName, docId, subCollName, subCollDocId, data) => {
  const docRef = doc(db, collName, docId, subCollName, subCollDocId);
  await updateDoc(docRef, data);
};
// END: UPDATE


// BEGIN: CREATE/POST
const createNewFormInDb = async (db, formData) => {
  const newFormRef = doc(collection(db, "Forms"));
  const formId = newFormRef.id;
  formData.id = formId;
  await setDoc(newFormRef, formData);
  return formData;
};

const createNewSubcollectionDocInDb = async (
  parentCollectionName,
  parentDocId,
  subCollectionName,
  subCollectionDocData,
) => {
  const ref = doc(collection(
    db,
    parentCollectionName,
    parentDocId,
    subCollectionName
  ));
  const id = ref.id;
  subCollectionDocData.id = id;
  await setDoc(ref, subCollectionDocData);
  return subCollectionDocData;
};

const createNewDocInDb = async (collName, data) => {
  const ref = doc(collection(db, collName));
  const id = ref.id;
  data.id = id;
  await setDoc(ref, data);
  return data;
};

const createDocumentWithId = async (collectionName, documentId) => {
  const collectionRef = collection(db, collectionName);
  await setDoc(doc(collectionRef, documentId), {});
};
// END: CREATE/POST


// BEGIN: DELETE
const deleteDocByCollectionAndId = async (collName, docId) => {
  await deleteDoc(doc(db, collName, docId));
};

const deleteDocsByCollectionAndFK = async (collName, fkLabel, fkValue) => {
  const collectionRef = collection(db, collName);
  const q = query(collectionRef, where(fkLabel, '==', fkValue));
  const querySnapshot = await getDocs(q);
  const deletePromises = [];

  querySnapshot.forEach((doc) => {
    const deletePromise = deleteDoc(doc.ref);
    deletePromises.push(deletePromise);
  });

  await Promise.all(deletePromises);
}

const deleteDocInSubCollection = async (collName, docId, subCollName, subCollDocId) => {
  const docRef = doc(db, collName, docId, subCollName, subCollDocId);
  await deleteDoc(docRef);
};

const deleteDocumentAndSubCollection = async (collName, docId, subCollName, subCollExists) => {
  const docRef = doc(db, collName, docId);

  if (subCollExists) {
    const subCollQuery = query(collection(docRef, subCollName));
    const subCollSnapshot = await getDocs(subCollQuery);
    subCollSnapshot.forEach((doc) => {
      deleteDoc(doc.ref);
    });
  }
  deleteDoc(docRef);
};

const deleteFormAndAllRelatedDocuments = async (formId, hasMembersCollection) => {
  const formRef = doc(db, 'Forms', formId);

  // Delete all documents in FormMembers subcollection
  if (hasMembersCollection) {
    const formMembersQuery = query(collection(formRef, 'FormMembers'));
    const formMembersSnapshot = await getDocs(formMembersQuery);
    formMembersSnapshot.forEach((doc) => {
      deleteDoc(doc.ref);
    });
  }

  // Get all SafetyAudits with formId
  const safetyAuditsQuery = query(collection(db, 'SafetyAudits'), where('formId', "==", formId));
  const safetyAuditsSnapshot = await getDocs(safetyAuditsQuery);
  const safetyAuditsIds = [];
  const safetyAuditsDocRefs = [];
  safetyAuditsSnapshot.forEach((doc) => {
    safetyAuditsDocRefs.push(doc.ref);
    safetyAuditsIds.push(doc.id);
  });

  // Delete all Spot Checks with a SafetyAudit id that is tied to the formId
  // safetyAuditsIds.forEach(async (id) => {
  for (const id of safetyAuditsIds) {
    const spotChecksQuery = query(collection(db, 'SpotChecks'), where('safetyAuditId', "==", id));
    const spotChecksSnapshot = await getDocs(spotChecksQuery);
    for (const doc of spotChecksSnapshot.docs) {
      await deleteDoc(doc.ref);
    }
  }

  // Delete all Safety Audits
  for (const doc of safetyAuditsSnapshot.docs) {
    await deleteDoc(doc.ref);
  }

  // Delete the form finally
  deleteDoc(formRef);
};
// END: DELETE


// BEGIN: MISC
const addTeamMembersToFormMembers = async (formId, teamMembers) => {
  try {
    await runTransaction(db, async (transaction) => {
      const formRef = doc(db, "Forms", formId);
      const formDoc = await transaction.get(formRef);
      if (!formDoc.exists()) {
        throw new Error("Form document does not exist.");
      }
      const formMembersRef = collection(db, "Forms", formId, 'FormMembers');


      const snapshot = await getDocs(formMembersRef);

      for (const doc of snapshot.docs) {
        await deleteDoc(doc.ref);
      }

      teamMembers.forEach((member) => {
        const newFormMemberRef = doc(formMembersRef, member.id);
        const formMember = {
          ...member,
          checkin: false, checkout: false,
          checkinTime: null, checkoutTime: null, equipment: ''
        };
        transaction.set(newFormMemberRef, formMember);
      });
    });
  } catch (error) {
    console.error(error);
  }
};

const getCountOfDocsInSubCollection = async (collName, docId, subCollName) => {
  const subCollectionRef = collection(db, collName, docId, subCollName);
  const snapshot = await getCountFromServer(subCollectionRef);
  return snapshot.data().count;
};
// END: MISC


export {
  auth,
  db,
  logInWithEmailAndPassword,
  registerWithEmailAndPassword,
  sendPasswordReset,
  logout,
  getDocByCollectionAndId,
  getDocsByCollectionAndFK,
  getAllDocsInCollection,
  getAllDocsBySubCollection,
  getDocsBySubCollectionAndIds,
  getUserByUid,
  createNewDocInDb,
  createNewSubcollectionDocInDb,
  createNewFormInDb,
  createDocumentWithId,
  addTeamMembersToFormMembers,
  getCountOfDocsInSubCollection,
  updateDocByCollectionAndId,
  updateUserByUid,
  updateDocInSubCollection,
  deleteDocByCollectionAndId,
  deleteDocsByCollectionAndFK,
  deleteDocInSubCollection,
  deleteDocumentAndSubCollection,
  deleteFormAndAllRelatedDocuments
};
